import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, Subject, interval } from 'rxjs';
import { map, debounceTime, filter, takeUntil, take } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { UnitTransmissionStatusService } from 'src/app/units/services/unit-transmission-status.service';
import { VehicleValidation, VehicleValidationStatus, EvidenceFile, EquipmentStatus } from '../models/vehicle-validation';
import { CriterionSelected, ValidationCriterion } from '../models/validation-criterion';
import { DynamicField, DynamicFiledSelected } from '../models/dynamic-field';


import {FleetChecklistStoreService} from '../store/fleet-checklist-store.service';
import { FleetChecklistApiService } from './fleet-checklist-api.service';
import {VehicleValidationManagerService} from './vehicle-validation-manager.service';

import {FleetChecklistHelper} from '../helpers/fleet-checklist.helper';


@Injectable({
  providedIn: 'root'
})
export class VehicleValidationEditorService {
  private vehiclesSubject = new BehaviorSubject<VehicleValidation[]>([]);
  public vehicles$: Observable<VehicleValidation[]> = this.vehiclesSubject.asObservable();

  private selectedVehicle = new BehaviorSubject<VehicleValidation | null>(null);
  public selectedVehicle$: Observable<VehicleValidation | null> = combineLatest([
    this.vehiclesSubject,
    this.selectedVehicle
  ]).pipe(
    map(([vehicles, selected]) => {
      if (!selected) return null;
      return vehicles.find(v => v.id == selected.id) || null;
    })
  );

  private searchTermSubject = new BehaviorSubject<string>('');
  public searchTerm$: Observable<string> = this.searchTermSubject.asObservable();

  private uploadEvidenceModalOpenSubject = new BehaviorSubject<boolean>(false);
  uploadEvidenceModalOpen$ = this.uploadEvidenceModalOpenSubject.asObservable();

  private isModifiedSubject = new BehaviorSubject<boolean>(false);
  public isModified$: Observable<boolean> = this.isModifiedSubject.asObservable();

  private saveTriggerSubject = new Subject<void>();

  private autoMarkingSubject = new BehaviorSubject<boolean>(false);
  public autoMarking$: Observable<boolean> = this.autoMarkingSubject.asObservable();

  private countdownSubject = new BehaviorSubject<number>(0);
  countdown$ = this.countdownSubject.asObservable();
  private stopCountdown$ = new Subject<void>();

  public filteredVehicles$: Observable<VehicleValidation[]> = combineLatest([
    this.vehiclesSubject,
    this.searchTermSubject
  ]).pipe(
    map(([vehicles, term]) => {
      if (!term) return vehicles;
      const query = term.toLowerCase().trim();
      return vehicles.filter(v =>
        (v.unitCode && v.unitCode.toLowerCase().includes(query)) ||
        (v.plate && v.plate.toLowerCase().includes(query))
      );
    })
  );

  constructor(
    private api: FleetChecklistApiService,
    private store: FleetChecklistStoreService,
    private transmission: UnitTransmissionStatusService,
    private vehicleValidationManager: VehicleValidationManagerService
  ) {
    this.store.vehicles$.subscribe(vehicles => {
      console.log('Fleet Checklist Vehicle update store', vehicles)
      let checklist = this.store.getSummary();
      if (!checklist || !vehicles) return;
      this.loadVehicles(
        vehicles
      );
    });
    this.transmission.getUnitsTransmissionStatus.subscribe((t) => {
      this.applyAutoMarkingTransmission(t, this.store.getValidationCriteria() || [])
    })


    this.saveTriggerSubject.subscribe(() => {
      this.stopCountdown$.next();
      this.countdownSubject.next(60);
      interval(1000).pipe(
        takeUntil(this.stopCountdown$),
        take(60)
      ).subscribe(i => {
        const value = 59 - i;
        this.countdownSubject.next(value);
        if (value <= 0) {
          this.stopCountdown$.next();
        }
      });
    });

    this.saveTriggerSubject.pipe(
      debounceTime(60000), // espera 60 segundos desde el último cambio
      filter(() => this.isModifiedSubject.getValue())
    ).subscribe(() => {
      this.onSaveValidation();
    });
  }

  get vehicles(): VehicleValidation[] {
    return this.vehiclesSubject.getValue();
  }

  setSelectedVehicle(vehicle: VehicleValidation | null) {
    this.selectedVehicle.next(vehicle);
  }

  getSelectedVehicle(): VehicleValidation | null {
    return this.selectedVehicle.getValue();
  }

  markAsModified() {
    this.isModifiedSubject.next(true);
    this.saveTriggerSubject.next();
  }

  openEvidenceModal(vehicle: VehicleValidation) {
    console.log("vehicles selected", vehicle)
    this.selectedVehicle.next(vehicle);
    this.uploadEvidenceModalOpenSubject.next(true);
  }

  closeEvidenceModal() {
    this.uploadEvidenceModalOpenSubject.next(false);
    this.selectedVehicle.next(null);
  }

  isVehiclesCompleted() : boolean {
    return this.vehiclesSubject.getValue().every(v => v.status == VehicleValidationStatus.completed || v.status == VehicleValidationStatus.inReview || v.status == VehicleValidationStatus.observation);
  }

  clear() {
    this.vehiclesSubject.next([]);
    this.isModifiedSubject.next(false);
    this.autoMarkingSubject.next(false);
    this.selectedVehicle.next(null);
  }

  loadVehicles(data: VehicleValidation[]) {
    const criteria = this.store.getValidationCriteria();
    const dynamicFields = this.store.getDynamicFields();
    const normalized = this.initializeVehicleFieldsAndCriteria(data, criteria, dynamicFields);
    console.log('Normalized vehicles:', normalized);
    this.vehiclesSubject.next(normalized);
  }

  setSearchTerm(term: string) {
    this.searchTermSubject.next(term);
  }

  private initializeVehicleFieldsAndCriteria(
    vehicles: VehicleValidation[],
    criteria: ValidationCriterion[],
    dynamicFields: DynamicField[]
  ): VehicleValidation[] {
    if (!vehicles) return [];
    return vehicles.map(vehicle => {
      // Normalize criteria from items if items is returned by backend
      let normalizedCriteria = vehicle.criteria || {};
      const normalized: Record<string, CriterionSelected> = {};
      Object.keys(normalizedCriteria).forEach(key => {
        const c = normalizedCriteria[key];
        normalized[key] = {
          ...c,
          selectedStateId:
            c.selectedStateId != null
              ? c.selectedStateId.toString()
              : ''
        };
      });
      normalizedCriteria = normalized;
      // Normalize dynamic fields (fields vs fileds)
      const fields = { ...(vehicle.fields || {}) };
      dynamicFields.forEach(df => {
        if (fields[df.id]) {
          fields[df.id] = {
            ...fields[df.id],
            type: df.type
          };
        } else {
          fields[df.id] = {
            id: df.id,
            value: null,
            type: df.type
          };
        }
      });
      const normalizedFieldsArray = FleetChecklistHelper.toInternalFields(fields);

      console.log('normalizedFieldsArray', normalizedFieldsArray);

      return {
        ...vehicle,
        criteria: normalizedCriteria,
        fields: normalizedFieldsArray
      };
    });
  }

  // EDIT VEHICLE VALIDATION
  updateFieldValue(vehicleId: string, fieldId: string, value: any): void {
    const vehicles = this.vehiclesSubject.getValue();
    const updated = vehicles.map(v => {
      if (v.id !== vehicleId) return v;
      return {
        ...v,
        fields: {
          ...v.fields,
          [fieldId]: {
            ...v.fields[fieldId],
            value
          }
        },
        modified: true
      };
    });
    this.vehiclesSubject.next(updated);
    this.markAsModified()
  }
  updateObservations(vehicleId: string, observations: string): void {
    const vehicles = this.vehiclesSubject.getValue();
    const updated = vehicles.map(v => {
      if (v.id === vehicleId) {
        return { ...v, observations, modified: true };
      }
      return v;
    });
    this.vehiclesSubject.next(updated);
    this.markAsModified();
  }
  toggleCriterionState(
    vehicleId: string,
    criterionId: string,
    criterionStates: any[],
    criterionName?: string
  ): void {
    if (!criterionStates?.length) return;

    const transmissionUnits =
      this.transmission.getUnitWithTransmissionStatus();

    const vehicles = this.vehiclesSubject.getValue();

    const updated = vehicles.map(v => {
      if (v.id !== vehicleId) return v;

      const selectedCrit = v.criteria?.[criterionId] ?? {
        id: criterionId,
        selectedStateId: ''
      };

      const currentId = selectedCrit.selectedStateId?.toString();

      let nextStateId: string;

      if (!currentId) {
        nextStateId = criterionStates[0].id.toString();
      } else {
        const currentIndex = criterionStates.findIndex(
          s => s.id.toString() === currentId
        );

        if (currentIndex === criterionStates.length - 1) {
          nextStateId = '';
        } else {
          nextStateId = criterionStates[currentIndex + 1].id.toString();
        }
      }

      let updatedVehicle: VehicleValidation = {
        ...v,
        criteria: {
          ...v.criteria,
          [criterionId]: {
            ...selectedCrit,
            selectedStateId: nextStateId
          }
        },
        modified: true
      };

      if (criterionName) {
        const name = criterionName.toLowerCase();

        if (name.includes('gps')) {
          updatedVehicle = this.applyTransmissionParameters(
            updatedVehicle,
            transmissionUnits,
            'gps'
          );
        } else if (name.includes('cipia')) {
          updatedVehicle = this.applyTransmissionParameters(
            updatedVehicle,
            transmissionUnits,
            'cipia'
          );
        }
      }

      return updatedVehicle;
    });

    console.log('Updated Toggle State vehicles:', updated);
    this.vehiclesSubject.next(updated);
    this.markAsModified()
  }

  resetVehicleCriteria(vehicleId: string): void {
    const vehicles = this.vehiclesSubject.getValue();

    const updated = vehicles.map(v => {
      if (v.id !== vehicleId) return v;
      const resetCriteria: any = {};
      Object.entries(v.criteria ?? {}).forEach(([key, value]) => {
        resetCriteria[key] = {
          ...value,
          selectedStateId: null
        };
      });

      return {
        ...v,
        criteria: resetCriteria,
        parameters: {},
        modified: true
      };
    });

    this.vehiclesSubject.next(updated);
    this.markAsModified();
  }

  async removeVehicle(vehicle : VehicleValidation): Promise<boolean> {
    const vehicles = this.vehiclesSubject.getValue();
    try {
      let res = await this.api.deleteVehicleValidation(vehicle.checklist_id);
      if(res){
        const updated = vehicles.filter(v => v.id.toString() !== vehicle.id.toString());
        this.vehiclesSubject.next(updated);
        this.store.updateChecklistVehicles(updated);
        return true;
      }else {
        return false;
      }
    } catch {
      return false
    }
  }
  toggleAutoMarking() {
    let isAutoMarking = this.autoMarkingSubject.getValue();
    if (isAutoMarking) {
      //this.disableAutoMarking();
    } else {
      this.enableAutoMarking();
    }
  }
  toggleVehicleValidationStatus(
    vehicleId: string,
    status: VehicleValidationStatus
  ): void {
    const vehicles = this.vehiclesSubject.getValue();

    const updated = vehicles.map(v => {
      if (v.id !== vehicleId) return v;

      const updatedFields = { ...v.fields };

      if (status == VehicleValidationStatus.completed || status == VehicleValidationStatus.inReview || status == VehicleValidationStatus.observation) {
        const field = updatedFields['validada'];

        if (field && (field.value === null || field.value === undefined || field.value === '')) {
          updatedFields['validada'] = {
            ...field,
            value: new Date()
          };
        }
      }

      return {
        ...v,
        status,
        fields: updatedFields,
        modified: true
      };
    });

    this.markAsModified();
    this.vehiclesSubject.next(updated);

    // if(status == VehicleValidationStatus.completed || status == VehicleValidationStatus.inReview || status == VehicleValidationStatus.observation){
    //   this.onSaveValidation();
    // }
  }

  async uploadEvidences(
    vehicle: VehicleValidation,
    evidences: EvidenceFile[]
  ): Promise<string[]> {

    const urls: string[] = [];
    for (const evidence of evidences) {
      const url = await this.api.uploadFleetEvidence(
        vehicle.checklist_id,
        evidence
      );
      if (url) urls.push(url)
    }
    const updatedVehicles = this.vehiclesSubject.getValue().map(v =>
      v.id === vehicle.id
        ? {
          ...v,
          parameters: {
            ...v.parameters,
            evidences: [
              ...(v.parameters?.evidences ?? []),
              ...urls
            ]
          }
        }
        : v
    );
    this.vehiclesSubject.next(updatedVehicles);
    return urls;
  }

  async removeEvidence(
    vehicle: VehicleValidation,
    evidenceUrl: string
  ): Promise<boolean> {
    const success = await this.api.removeFleetEvidence(
      vehicle.checklist_id,
      evidenceUrl
    );
    if (!success) return false;

    const updatedVehicles = this.vehiclesSubject.getValue().map(v => {
      if (v.id === vehicle.id) {
        const evidences = (v.evidences || []).filter((url: string) => url !== evidenceUrl);
        const paramEvidences = (v.parameters?.evidences || []).filter((url: string) => url !== evidenceUrl);
        return {
          ...v,
          evidences,
          parameters: {
            ...v.parameters,
            evidences: paramEvidences
          }
        };
      }
      return v;
    });

    this.vehiclesSubject.next(updatedVehicles);
    return true;
  }

  // AUTO MARKGIN
  async enableAutoMarking() {
    this.autoMarkingSubject.next(true);

    // 1. Transmission GPS Y CIPIA
    let transmissionUnits = this.transmission.getUnitWithTransmissionStatus();
    this.applyAutoMarkingTransmission(transmissionUnits, this.store.getValidationCriteria() || [])

    let vehicles = this.vehiclesSubject.getValue();
    if (!vehicles.length) return;
    let equipmentStatus = await this.vehicleValidationManager.getEquipmentStatus(vehicles);
    if(!equipmentStatus) return;
    this.applyAutoMarkingEquipment(equipmentStatus, this.store.getValidationCriteria() || [])
    setTimeout(() => {
      this.disableAutoMarking();
    }, 600);
  }
  disableAutoMarking() {
    this.autoMarkingSubject.next(false);
  }

  applyAutoMarkingTransmission(transmissionUnits: any[], vehicleCriteria: ValidationCriterion[]): void {

    if (!this.autoMarkingSubject.value || !transmissionUnits?.length) return;

    let vehicles = this.vehiclesSubject.getValue();

    if (!vehicles.length) return;

    const gpsCriterion = vehicleCriteria?.find(c =>
      c.name.toLowerCase().includes('gps')
    );

    const cipiaCriterion = vehicleCriteria?.find(c =>
      c.name.toLowerCase().includes('cipia')
    );

    const getStates = (criterion: any) => {
      if (!criterion?.states?.length) return {};

      return {
        active: criterion.states.find((s: any) =>
          ['activo', 'operativo', 'ok'].some(x =>
            s.name.toLowerCase().includes(x)
          )
        ),
        inactive: criterion.states.find((s: any) =>
          ['inact', 'rev', 'sin'].some(x =>
            s.name.toLowerCase().includes(x)
          )
        )
      };
    };

    const gpsStates = getStates(gpsCriterion);
    const cipiaStates = getStates(cipiaCriterion);

    let isModified = false;
    const updated = vehicles.map(v => {
      if (v.status == VehicleValidationStatus.completed || v.status == VehicleValidationStatus.inReview || v.status == VehicleValidationStatus.observation) {
        return v;
      }
      let v1 = this.applyAutoCriteriaTransmissionMarking(
        v,
        transmissionUnits,
        vehicleCriteria,
        gpsStates,
        cipiaStates
      );

      let v2 = this.applyTransmissionParameters(
        v1,
        transmissionUnits
      );
      isModified = true;
      return v2;
    });
    console.log('Auto Marking Updated vehicle:', updated);

    this.vehiclesSubject.next(updated);
    if (isModified) this.markAsModified();
  }

  applyAutoMarkingEquipment(equipmentStatus: EquipmentStatus[] ,vehicleCriteria: ValidationCriterion[]): void {
    if (!this.autoMarkingSubject.value) return;
    let vehicles = this.vehiclesSubject.getValue();
    if (!vehicles.length) return;
    const adasCriterion = vehicleCriteria?.find(c =>
      c.name.toLowerCase().includes('adas')
    );
    const alcoholCriterion = vehicleCriteria?.find(c =>
      c.name.toLowerCase().includes('alco')
    );
    const getStates = (criterion: any) => {
      if (!criterion?.states?.length) return {};
      return {
        active: criterion.states.find((s: any) =>
          ['ok'].some(x =>
            s.name.toLowerCase().includes(x)
          )
        ),
        inactive: criterion.states.find((s: any) =>
          ['rev'].some(x =>
            s.name.toLowerCase().includes(x)
          )
        ),
        warning: criterion.states.find((s: any) =>
          ['obs'].some(x =>
            s.name.toLowerCase().includes(x)
          )
        )
      };
    };

    const adasStates = getStates(adasCriterion);
    const alcoholStates = getStates(alcoholCriterion);

    let isModified = false;
    const updated = vehicles.map(v => {
      console.log('Auto Marking Updated vehicle:', v);
      if (v.status == VehicleValidationStatus.completed || v.status == VehicleValidationStatus.inReview || v.status == VehicleValidationStatus.observation) {
        return v;
      }
      const criteria = { ...(v.criteria || {}) };
      let equipment = equipmentStatus.find(e => e.plate == v.plate);
      console.log('equipment:', equipment, equipmentStatus, v);
      if (!equipment) return v;

      const parameters = {
        ...v.parameters,
        equipmentStatus: {
          ...(v.parameters?.equipmentStatus || {})
        }
      };

      let hasChanges = false;
      let today = new Date();
      if(adasCriterion && equipment.adas){
        let adasDate = new Date(equipment.adas);

        adasDate.setHours(0, 0, 0, 0);

        const diffTime = today.getTime() - adasDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if(diffDays <= 2){
          if (adasStates.active) {
            criteria[adasCriterion.id] = {
              ...criteria[adasCriterion.id],
              selectedStateId: adasStates.active.id.toString()
            };
            hasChanges = true;
          }
        }else if(diffDays <= 5){
          if (adasStates.warning) {
            criteria[adasCriterion.id] = {
              ...criteria[adasCriterion.id],
              selectedStateId: adasStates.warning.id.toString()
            };
            hasChanges = true;
          }
        } else {
          if (adasStates.inactive) {
            criteria[adasCriterion.id] = {
              ...criteria[adasCriterion.id],
              selectedStateId: adasStates.inactive.id.toString()
            };
            hasChanges = true;
          }
        }

        parameters.equipmentStatus['adas'] = {
          lastEventDate: equipment.adas,
          lastEventName: equipment.adasEventName
        };
        hasChanges = true;
      } else {
        if (adasCriterion && adasStates.inactive) {
          criteria[adasCriterion.id] = {
            ...criteria[adasCriterion.id],
            selectedStateId: adasStates.inactive.id.toString()
          };
          hasChanges = true;
        }
      }
      if(alcoholCriterion && equipment.alcohol){
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const alcoholDate = new Date(equipment.alcohol);
        alcoholDate.setHours(0, 0, 0, 0);

        const diffTime = today.getTime() - alcoholDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if(diffDays <= 2){
          if (alcoholStates.active) {
            criteria[alcoholCriterion.id] = {
              ...criteria[alcoholCriterion.id],
              selectedStateId: alcoholStates.active.id.toString()
            };
            hasChanges = true;
          }
        }else if(diffDays <= 5){
          if (alcoholStates.warning) {
            criteria[alcoholCriterion.id] = {
              ...criteria[alcoholCriterion.id],
              selectedStateId: alcoholStates.warning.id.toString()
            };
            hasChanges = true;
          }
        }else{
          if (alcoholStates.inactive) {
            criteria[alcoholCriterion.id] = {
              ...criteria[alcoholCriterion.id],
              selectedStateId: alcoholStates.inactive.id.toString()
            };
            hasChanges = true;
          }
        }

        parameters.equipmentStatus['alcohol'] = {
          lastEventDate: equipment.alcohol,
          lastEventName: equipment.alcoholEventName
        };
        hasChanges = true;
      } else {
        if (alcoholCriterion && alcoholStates.inactive) {
          criteria[alcoholCriterion.id] = {
            ...criteria[alcoholCriterion.id],
            selectedStateId: alcoholStates.inactive.id.toString()
          };
          hasChanges = true;
        }
      }


      if (hasChanges) {
        isModified = true;
        return {
          ...v,
          criteria,
          parameters,
          modified: true
        };
      }
      return v;
    });
    console.log('Auto Marking Updated vehicle:', updated);
    this.vehiclesSubject.next(updated);
    if (isModified) this.markAsModified();
  }

  private applyAutoCriteriaTransmissionMarking(
    vehicle: VehicleValidation,
    transmissionUnits: any[],
    vehicleCriteria: ValidationCriterion[],
    gpsStates: any,
    cipiaStates: any
  ): VehicleValidation {
    const criteria = { ...(vehicle.criteria || {}) };

    const matchedUnits = transmissionUnits.filter(unit => {
      const uPlate = unit.licensePlate?.toLowerCase().trim();
      const vPlate = vehicle.plate?.toLowerCase().trim();
      return uPlate && vPlate && uPlate === vPlate;
    });

    if (matchedUnits.length === 0) return vehicle;

    matchedUnits.forEach(unit => {
      const isCipia = unit.GNSSIMEI?.toUpperCase().startsWith('E');

      const criterion = isCipia
        ? vehicleCriteria.find(c => c.name.toLowerCase().includes('cipia'))
        : vehicleCriteria.find(c => c.name.toLowerCase().includes('gps'));

      const states = isCipia ? cipiaStates : gpsStates;

      if (!criterion) return;

      const vehicleCrit = criteria[criterion.id];
      if (!vehicleCrit) return;

      const statusId = unit.transmissionStatusId;

      if ([1, 2].includes(statusId)) {
        if (states.active) {
          criteria[criterion.id] = {
            ...vehicleCrit,
            selectedStateId: states.active.id.toString()
          };
        }
      } else {
        if (states.inactive) {
          criteria[criterion.id] = {
            ...vehicleCrit,
            selectedStateId: states.inactive.id.toString()
          };
        }
      }
    });

    return {
      ...vehicle,
      criteria,
      modified: true
    };
  }
  private applyTransmissionParameters(
    vehicle: VehicleValidation,
    transmissionUnits: any[],
    trackerType?: 'gps' | 'cipia'
  ): VehicleValidation {

    const matchedUnits = transmissionUnits.filter(unit => {
      const uPlate = unit.licensePlate?.toLowerCase().trim();
      const vPlate = vehicle.plate?.toLowerCase().trim();

      if (!uPlate || !vPlate || uPlate !== vPlate) {
        return false;
      }

      const isCipia = unit.GNSSIMEI?.toUpperCase().startsWith('E');

      if (trackerType === 'cipia') {
        return isCipia;
      }

      if (trackerType === 'gps') {
        return !isCipia;
      }

      // Sin filtro: procesa ambos
      return true;
    });

    if (matchedUnits.length === 0) return vehicle;

    const parameters = {
      ...vehicle.parameters,
      transmission: {
        ...(vehicle.parameters?.transmission || {})
      }
    };

    matchedUnits.forEach(unit => {
      const isCipia = unit.GNSSIMEI?.toUpperCase().startsWith('E');
      const tracker = isCipia ? 'cipia' : 'gps';

      console.log('Applying transmission parameters for tracker:', tracker, unit);

      parameters.transmission[tracker] = {
        statusId: unit.transmissionStatusId,
        elapsedTime: unit.elapsedTime,
        lastStartTime: unit.device?.trackerDate
      };
    });

    return {
      ...vehicle,
      parameters,
      modified: true
    };
  }

  async onFinishValidation() : Promise<boolean> {
    let saved = await this.onSaveValidation();
    if (saved) {
      let checklistId = this.store.getChecklistId();
      let finished = await this.api.finishFleetChecklist(checklistId);
      if (finished) {
        return true
      }
    }
    return false
  }
  async onSaveValidation(): Promise<boolean> {
    let vehicles = this.vehiclesSubject.getValue();
    let vehiclesPrepared = vehicles.map(v => {
      let fields = FleetChecklistHelper.toOutputFields(v.fields || {});
      return {
        ...v,
        fields
      }
    })
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: toast => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });
    try {
      let modifiedVehicles = vehiclesPrepared.filter(v => v.modified === true);
      if (modifiedVehicles.length === 0) {
        console.log('No vehicles have been modified. Skipping save.');
        Toast.fire({
          icon: 'warning',
          title: 'No hay vehículos modificados.'
        });
        return true;
      }
      let checklistId = this.store.getChecklistId();
      const result = await this.api.updateFleetVehiclesValidation(checklistId, modifiedVehicles);
      if (result) {
        this.store.updateTagsByVehicles(vehiclesPrepared);
        console.log('Saved successfully');
        Toast.fire({
          icon: 'success',
          title: 'Los cambios se guardaron correctamente.'
        });
        const resetVehicles = vehicles.map(v => ({ ...v, modified: false }));
        this.vehiclesSubject.next(resetVehicles);
        this.isModifiedSubject.next(false);
        this.stopCountdown$.next();
        this.countdownSubject.next(0);
        return true;
      }
      Toast.fire({
        icon: 'error',
        title: 'No fue posible guardar los cambios.'
      });
      return false;
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: 'Ocurrió un problema al guardar.'
      });
      console.error('Error saving validations:', error);
      return false;
    }
  }
}
